import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { useIsBelowDesktop } from '@/lib/hooks/useBreakpoint';
import { useFormPanel } from './formPanelStore';
import { FormPanelHost } from './FormPanelHost';
import { useJourneyPreview } from './journeyPreviewStore';

/*
 * Los topes del carril.
 *
 * `MIN_WIDTH` no es un gusto estético: por debajo de ~300 px los campos del
 * formulario dejan de ser usables y el pie —«Eliminar proyecto · Cancelar ·
 * Guardar cambios»— ya no cabe ni en dos líneas sin apretar. El mínimo viejo
 * eran 240 px, y ahí el pie se salía por el borde derecho de la pantalla: el
 * botón de guardar desaparecía sin más, porque los botones llevan
 * `whitespace-nowrap` y el `aside` está anclado a la derecha.
 *
 * El tope de ancho NO puede ser la única defensa, y por eso no se subió el
 * mínimo hasta los ~480 px que pide ese pie en español: eso habría dejado el
 * carril prácticamente sin recorrido (480→560) y una traducción más larga lo
 * habría roto igual. La garantía de que nada se oculte vive en el pie de cada
 * formulario, que ahora envuelve (`flex-wrap`); estos topes sólo acotan el
 * recorrido a lo que tiene sentido.
 */
const MIN_WIDTH = 300;
const MAX_WIDTH = 560;
const DEFAULT_WIDTH = 360;
const COLLAPSED_WIDTH = 48;

/** Acota un ancho a los topes del carril. */
export function clampRailWidth(w: number): number {
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, w));
}

interface RightSidebarStore {
  open: boolean;
  width: number;
  toggle: () => void;
  setOpen: (open: boolean) => void;
  setWidth: (w: number) => void;
}

/**
 * The right rail is WORK SPACE, not a home for Atledi.
 *
 * It is painted with the CONTENT palette, not the chrome one: what it hosts is
 * a form or a preview, so it should read as an extension of the page rather
 * than as part of the frame. (It used to use the dark nav background, which
 * left its own labels dark-on-dark once the canvas went light.)
 *
 * It hosts, in priority order: an in-context entity form (FormPanelHost) or
 * the Design Journey live preview. With neither active it shows an "available"
 * placeholder and stays out of the way. Atledi used to live here and crowded
 * the rail out of usefulness; it now opens from its own compact launcher
 * (`AssistantLauncher` → `AssistantDrawer`), which is also where the
 * `aiAssistant` plan gate lives.
 *
 * Persist-key is `xotski.rightSidebar.v2` (bumped from v1 which defaulted
 * `open:true` for an activity feed) — existing users start collapsed.
 */
export const useRightSidebar = create<RightSidebarStore>()(
  persist(
    (set) => ({
      open: false,
      width: DEFAULT_WIDTH,
      toggle: () => set((s) => ({ open: !s.open })),
      setOpen: (open) => set({ open }),
      setWidth: (w) => set({ width: clampRailWidth(w) }),
    }),
    { name: 'xotski.rightSidebar.v2' },
  ),
);

/**
 * El hueco que el `AdminShell` reserva a la derecha.
 *
 * **Cero cuando el carril no tiene nada que enseñar.** Antes devolvía siempre
 * al menos `COLLAPSED_WIDTH`, así que en TODAS las pantallas —las que nunca
 * acoplan un formulario ni una previa— quedaba una franja de 48 px comiendo
 * ancho y sin nada dentro; ni siquiera traía el botón para plegarla, porque
 * ése sólo se pinta cuando hay contenido. Un borde que no hace nada y no se
 * puede quitar.
 *
 * Con contenido y plegado sí quedan los 48 px: ahí el borde es la manija para
 * volver a abrirlo.
 */
export function useRightRailOffset(): number {
  const open = useRightSidebar((s) => s.open);
  const width = useRightSidebar((s) => s.width);
  const formActive = useFormPanel((s) => s.active);
  const journeyActive = useJourneyPreview((s) => s.active);
  if (!formActive && !journeyActive) return 0;
  return open ? clampRailWidth(width) : COLLAPSED_WIDTH;
}

export function RightSidebar() {
  const t = useT();
  const open = useRightSidebar((s) => s.open);
  const width = useRightSidebar((s) => s.width);
  const toggle = useRightSidebar((s) => s.toggle);
  const setWidth = useRightSidebar((s) => s.setWidth);
  const isBelowDesktop = useIsBelowDesktop();
  // When a form is active the rail hosts that form instead of the assistant.
  const formActive = useFormPanel((s) => s.active);
  const closeForm = useFormPanel((s) => s.closeForm);
  // When the Design Journey tab is open the rail hosts its live preview
  // (portaled in from SurveyConfigSection) instead of the assistant.
  const journeyActive = useJourneyPreview((s) => s.active);
  const setJourneySlot = useJourneyPreview((s) => s.setSlot);

  const dragging = useRef(false);

  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!dragging.current) return;
      setWidth(window.innerWidth - e.clientX);
    }
    function onUp() {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [setWidth]);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // The rail only earns its width when it has work content to host.
  const hayContenido = !!formActive || journeyActive;
  const expanded = open && hayContenido;
  // Acotado al pintar, no sólo al arrastrar: quien ya tenga un ancho guardado
  // de antes (el mínimo eran 240 px) lo arrastra en localStorage, y `setWidth`
  // no llega a ejecutarse hasta que vuelva a tirar del borde.
  const renderedWidth = expanded ? clampRailWidth(width) : COLLAPSED_WIDTH;

  // Below lg the rail has no docked form — work content opens as a
  // full-height overlay sheet. With nothing to host there is nothing to show,
  // so the sheet never opens empty on a phone.
  if (isBelowDesktop) {
    if (!open || (!formActive && !journeyActive)) return null;
    return (
      <div className="fixed inset-0 z-50">
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={toggle}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />
        <div className="absolute inset-y-0 right-0 flex w-full max-w-sm flex-col border-l border-[var(--color-border-default)] bg-[var(--color-bg-surface)] text-[var(--color-fg-primary)] shadow-2xl">
          <div className="flex h-14 flex-none items-center gap-2 border-b border-[var(--color-xk-border)] px-4 text-[var(--color-xk-lavender)]">
            <span
              className="flex-1 text-left text-[11px] font-extrabold uppercase tracking-[0.12em]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {formActive ? formActive.title : t('journeyPreview.title')}
            </span>
            <button
              type="button"
              onClick={formActive ? () => closeForm() : toggle}
              aria-label={t('common.close')}
              className="grid h-8 w-8 flex-none place-items-center rounded-md text-[var(--color-xk-text-muted)] transition-colors hover:text-[var(--color-accent-ink)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex flex-1 min-h-0 flex-col">
            {formActive ? (
              <FormPanelHost />
            ) : (
              <div ref={setJourneySlot} className="flex flex-1 min-h-0 flex-col overflow-y-auto" />
            )}
          </div>
        </div>
      </div>
    );
  }

  /*
   * Sin nada que hospedar, el carril NO se monta.
   *
   * Plegado seguía pintando su borde de 48 px con el iconito y nada más, en
   * cada pantalla de la aplicación. El carril no tiene voz propia —hospeda un
   * formulario o una previa—, así que cuando no hay ninguna de las dos lo
   * correcto es desaparecer, no encogerse.
   */
  if (!hayContenido) return null;

  return (
    <aside
        className="fixed inset-y-0 right-0 z-40 hidden flex-col border-l border-[var(--color-border-default)] bg-[var(--color-bg-surface)] text-[var(--color-fg-primary)] transition-[width] duration-150 lg:flex"
        style={{ width: renderedWidth }}
      >
      {expanded && (
        <div
          onMouseDown={startDrag}
          aria-hidden="true"
          className="absolute inset-y-0 left-0 z-10 w-1 cursor-col-resize bg-transparent transition-colors hover:bg-[var(--color-accent-30)]"
        />
      )}

      {/*
        La cabecera sólo tiene sentido abierta: plegado, el carril es una
        franja de 48 px con la manija. El icono de «panel» que vivía aquí se
        fue al TopBar, junto al selector de aplicaciones: desde ahí se abre y
        se pliega igual que con la manija, y no ocupa sitio dentro de lo que
        pretende controlar.
      */}
      {expanded && (
      <div
        className={cn(
          'flex h-14 flex-none items-center border-b border-[var(--color-xk-border)] text-[var(--color-xk-lavender)]',
          'gap-2 px-4',
        )}
      >
        {expanded && (
          <span
            className="flex-1 text-left text-[11px] font-extrabold uppercase tracking-[0.12em]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {formActive
              ? formActive.title
              : journeyActive
                ? t('journeyPreview.title')
                : t('rightRail.title')}
          </span>
        )}
        {expanded && formActive && (
          <button
            type="button"
            onClick={() => closeForm()}
            aria-label={t('common.cancel')}
            className="grid h-8 w-8 flex-none place-items-center rounded-md text-[var(--color-xk-text-muted)] transition-colors hover:text-[var(--color-accent-ink)]"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      )}

      {(!!formActive || journeyActive) && (
      <button
        type="button"
        onClick={toggle}
        aria-label={expanded ? t('rightRail.collapse') : t('rightRail.expand')}
        className="absolute -left-3 top-1/2 z-50 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] text-[var(--color-xk-lavender)] shadow hover:text-[var(--color-accent-ink)]"
      >
        {expanded ? (
          <ChevronRight className="h-3.5 w-3.5" />
        ) : (
          <ChevronLeft className="h-3.5 w-3.5" />
        )}
      </button>
      )}

      {expanded ? (
        <div className="flex flex-1 min-h-0 flex-col">
          {formActive ? (
            <FormPanelHost />
          ) : journeyActive ? (
            <div ref={setJourneySlot} className="flex flex-1 min-h-0 flex-col overflow-y-auto" />
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}

